import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  User,
  Target,
  Phone,
  Mail,
  Calendar,
  Flame,
  X,
  Loader2,
  ChevronRight,
  TrendingUp,
  MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

// Smart phone number normalization - handles all common formats
const normalizePhone = (phone) => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');

  // Handle different lengths:
  // 10 digits: 5551234567 → 5551234567
  // 11 digits starting with 1: 15551234567 → 5551234567
  // 12+ digits: +15551234567 → 5551234567
  if (digits.length === 10) {
    return digits;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return digits.substring(1);
  } else if (digits.length >= 11) {
    // Take last 10 digits
    return digits.substring(digits.length - 10);
  }

  return digits;
};

// Format phone for display: 5551234567 → (555) 123-4567
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const normalized = normalizePhone(phone);
  if (normalized.length === 10) {
    return `(${normalized.substring(0, 3)}) ${normalized.substring(3, 6)}-${normalized.substring(6)}`;
  }
  return phone;
};

// Search leads with smart phone matching
const searchLeads = async (query, tenantId, isGlobalAdmin = false) => {
  if (!query || (!tenantId && !isGlobalAdmin)) return [];

  try {
    const normalizedQuery = normalizePhone(query);
    console.log('🔍 Search query:', query);
    console.log('📞 Normalized phone:', normalizedQuery);
    console.log('🏢 Tenant ID:', tenantId, '| Global admin:', isGlobalAdmin);

    // Build search query
    let searchQuery = supabase
      .from('leads')
      .select('id, name, first_name, last_name, email, phone, phone_digits, status, created_at, tenant_id')
      .limit(10);

    // Only filter by tenant for non-global-admin users
    if (!isGlobalAdmin) {
      searchQuery = searchQuery.eq('tenant_id', tenantId);
    }

    // Phone-shaped input → search phone_digits only. Text input → name/email.
    // Strip ( ) , from text queries since those break PostgREST or() syntax.
    const isPhoneSearch = /^[\d\s()\-+.]+$/.test(query.trim()) && normalizedQuery.length >= 3;

    if (isPhoneSearch) {
      searchQuery = searchQuery.ilike('phone_digits', `%${normalizedQuery}%`);
    } else {
      const safe = query.toLowerCase().replace(/[(),]/g, ' ').replace(/\s+/g, ' ').trim();
      if (!safe) return [];
      searchQuery = searchQuery.or(
        `email.ilike.%${safe}%,name.ilike.%${safe}%,first_name.ilike.%${safe}%,last_name.ilike.%${safe}%`
      );
    }

    const { data, error } = await searchQuery;

    if (error) {
      console.error('❌ Search error:', error);
      throw error;
    }

    console.log('✅ Search results:', data);
    return data || [];
  } catch (error) {
    console.error('❌ Error searching leads:', error);
    return [];
  }
};

// Search campaigns
const searchCampaigns = async (query, tenantId, isGlobalAdmin = false) => {
  if (!query || (!tenantId && !isGlobalAdmin)) return [];

  try {
    let campaignsQuery = supabase
      .from('campaigns')
      .select('id, name, is_active, created_at, tenant_id')
      .ilike('name', `%${query}%`)
      .limit(10);

    // Only filter by tenant for non-global-admin users
    if (!isGlobalAdmin) {
      campaignsQuery = campaignsQuery.eq('tenant_id', tenantId);
    }

    const { data, error } = await campaignsQuery;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error searching campaigns:', error);
    return [];
  }
};

const GlobalSearchPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState({ leads: [], campaigns: [] });
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const { user, role } = useAuth();

  // Debounced search
  useEffect(() => {
    const isGlobalAdmin = role === 'global_admin';
    if (!isOpen || !query.trim() || (!user?.tenant_id && !isGlobalAdmin)) {
      setResults({ leads: [], campaigns: [] });
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      const [leads, campaigns] = await Promise.all([
        searchLeads(query, user.tenant_id, isGlobalAdmin),
        searchCampaigns(query, user.tenant_id, isGlobalAdmin)
      ]);

      setResults({ leads, campaigns });
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, isOpen, user?.tenant_id, role]);

  // Focus input and clear query when opened
  useEffect(() => {
    if (isOpen) {
      setQuery(''); // Clear previous search
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen]);

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    const items = [];
    results.leads.forEach(lead => items.push({ type: 'lead', data: lead }));
    results.campaigns.forEach(campaign => items.push({ type: 'campaign', data: campaign }));
    return items;
  }, [results]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (flatResults[selectedIndex]) {
          handleResultClick(flatResults[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flatResults, selectedIndex, onClose]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatResults]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && flatResults[selectedIndex]) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, flatResults]);

  const handleResultClick = (result) => {
    if (result.type === 'lead') {
      navigate(`/lead/${result.data.id}`);
    } else if (result.type === 'campaign') {
      navigate(`/campaign-management?id=${result.data.id}`);
    }
    onClose();
  };

  const highlightMatch = (text, query) => {
    if (!query.trim() || !text) return text;

    try {
      const parts = text.toString().split(new RegExp(`(${query})`, 'gi'));
      return parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-slate-900 font-medium">
            {part}
          </mark>
        ) : (
          part
        )
      );
    } catch {
      return text;
    }
  };

  const getLeadName = (lead) => {
    // Check for 'name' field first, then fall back to first_name + last_name
    if (lead.name) return lead.name;
    const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
    return fullName || 'Unnamed Lead';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Hot Lead': 'text-orange-600 bg-orange-50 border-orange-200',
      'Warm Lead': 'text-amber-600 bg-amber-50 border-amber-200',
      'Cold Lead': 'text-blue-600 bg-blue-50 border-blue-200',
      'Won': 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'Lost': 'text-slate-600 bg-slate-50 border-slate-200',
      'Opted Out': 'text-red-600 bg-red-50 border-red-200'
    };
    return colors[status] || 'text-slate-600 bg-slate-50 border-slate-200';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />

        {/* Search Palette */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search leads (name, phone, email) or campaigns..."
              className="flex-1 bg-transparent text-slate-900 placeholder-slate-400 outline-none text-base"
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded border border-slate-200">
                ⌘/
              </kbd>
              <button
                onClick={onClose}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isSearching ? (
              <div className="px-4 py-12 text-center">
                <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-3 animate-spin" />
                <p className="text-slate-600 text-sm">Searching...</p>
              </div>
            ) : !query.trim() ? (
              <div className="px-4 py-12 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Search for leads or campaigns</p>
                <p className="text-sm text-slate-400 mt-1">
                  Try searching by name, phone number, or email
                </p>
              </div>
            ) : flatResults.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No results found</p>
                <p className="text-sm text-slate-400 mt-1">
                  Try different keywords or check spelling
                </p>
              </div>
            ) : (
              <div ref={resultsRef} className="py-2">
                {/* Leads Section */}
                {results.leads.length > 0 && (
                  <div className="mb-2">
                    <div className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                      <User className="w-3.5 h-3.5" />
                      Leads ({results.leads.length})
                    </div>
                    {results.leads.map((lead, index) => {
                      const flatIndex = index;
                      const isSelected = flatIndex === selectedIndex;

                      return (
                        <div key={lead.id} className="px-2">
                          <button
                            onClick={() => handleResultClick({ type: 'lead', data: lead })}
                            className={`w-full text-left px-3 py-3 rounded-xl transition-all ${
                              isSelected
                                ? 'bg-slate-100 shadow-sm'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <div className={`
                                w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                                ${lead.status === 'Hot Lead' ? 'bg-orange-50 border-orange-200' : 'bg-slate-100 border-slate-200'} border
                              `}>
                                {lead.status === 'Hot Lead' ? (
                                  <Flame className="w-5 h-5 text-orange-600" />
                                ) : (
                                  <User className="w-5 h-5 text-slate-600" />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 mb-1">
                                  {highlightMatch(getLeadName(lead), query)}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                                  {lead.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {highlightMatch(formatPhoneDisplay(lead.phone), query)}
                                    </span>
                                  )}
                                  {lead.email && (
                                    <>
                                      {lead.phone && <span className="text-slate-300">·</span>}
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {highlightMatch(lead.email, query)}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(lead.status)}`}>
                                  {lead.status}
                                </span>
                              </div>

                              {/* Arrow */}
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 self-center" />
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Campaigns Section */}
                {results.campaigns.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                      <Target className="w-3.5 h-3.5" />
                      Campaigns ({results.campaigns.length})
                    </div>
                    {results.campaigns.map((campaign, index) => {
                      const flatIndex = results.leads.length + index;
                      const isSelected = flatIndex === selectedIndex;

                      return (
                        <div key={campaign.id} className="px-2">
                          <button
                            onClick={() => handleResultClick({ type: 'campaign', data: campaign })}
                            className={`w-full text-left px-3 py-3 rounded-xl transition-all ${
                              isSelected
                                ? 'bg-slate-100 shadow-sm'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                                <Target className="w-5 h-5 text-blue-600" />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 mb-1">
                                  {highlightMatch(campaign.name, query)}
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                                    campaign.is_active
                                      ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
                                      : 'text-slate-600 bg-slate-50 border-slate-200'
                                  }`}>
                                    {campaign.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              </div>

                              {/* Arrow */}
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 self-center" />
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">↑↓</kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">↵</kbd>
                <span>Open</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">ESC</kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="hidden sm:block">
              {flatResults.length} {flatResults.length === 1 ? 'result' : 'results'}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default GlobalSearchPalette;
